import React, { useEffect, useRef } from 'react';
import { ArrowUp } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { useEinsteinChat } from '../hooks/useEinsteinChat';
import EinsteinMessage from './EinsteinMessage';

// Home page redesign — the compact Einstein console shell embedded in Home's lower half,
// wired to the REAL router (POST /api/einstein/query, services/einsteinRouter.js) rather than
// the design handoff's assumed free-text completion endpoint. See README.md's "Read this before
// touching the design files" note: visual treatment only comes from the handoff; every behavior
// here is governed by what Phase 0/1 actually built (four response shapes, role-gating, tier
// rules). State machine + response-shape rendering live in useEinsteinChat.js/EinsteinMessage.js,
// shared with the dedicated full-page Einstein tab (pages/EinsteinChat.js, 2026-09-03) — this
// file owns only the compact rail+conversation layout.

export default function EinsteinConsole({ user, goToRoute }) {
  const { theme } = useTheme();
  const isDay = theme === 'day';
  const accent = isDay ? '#0891b2' : '#22d3ee';
  const railBg = isDay
    ? 'linear-gradient(170deg, rgba(20,49,63,.9) 0%, rgba(13,31,42,.9) 100%)'
    : 'linear-gradient(170deg, rgba(20,49,63,.82) 0%, rgba(13,31,42,.82) 100%)';
  const shellBg = isDay ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.05)';
  const einsteinBubbleBg = isDay ? 'rgba(20,24,28,.05)' : 'rgba(255,255,255,.06)';
  const coachBubbleBg = accent;
  const textColor = isDay ? '#1c2024' : '#fff';
  const mutedColor = isDay ? 'rgba(20,24,28,.55)' : 'rgba(255,255,255,.55)';

  const { thread, draft, setDraft, status, suggestions, hasSessionToken, hasChatted, send, onOptionClick } =
    useEinsteinChat({ user });

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const shellRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread, status]);

  // `height: 100%` (not minHeight) — this fills whatever fixed box Home's flex-column wrapper
  // gives it, which is what makes the message list's own overflowY:auto (below) actually the
  // thing that scrolls, instead of the whole page. With only a minHeight, an unconstrained flex
  // row just grows with its content forever and internal scroll never engages — that was the
  // second half of the "I have to scroll to reach the composer" bug (2026-09-03): the console
  // itself had no ceiling, so it (and the page) kept growing instead of the thread scrolling.
  return (
    <div ref={shellRef} style={{
      display: 'flex', overflow: 'hidden', borderRadius: 20, height: '100%',
      background: shellBg, backdropFilter: 'blur(18px)',
      boxShadow: isDay ? '0 -2px 0 rgba(8,145,178,.18), 0 20px 44px rgba(24,40,56,.14)' : '0 -2px 0 rgba(34,211,238,.18), 0 20px 44px rgba(0,0,0,.4)',
    }}>
      {/* character rail */}
      <div style={{ flex: 'none', width: 244, background: railBg, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '18px 20px 20px', gap: 12, position: 'relative' }}>
        <img src={`${process.env.PUBLIC_URL}/${encodeURIComponent('Einstein PF.png')}`} alt="Einstein"
          style={{
            width: 120, height: 120, borderRadius: '999px', flex: 'none', objectFit: 'cover',
            boxShadow: `inset 0 0 0 2px ${accent}55`,
          }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <span style={{ fontFamily: "'Archivo Black','Arial Black',Impact,sans-serif", fontSize: 24, letterSpacing: '.03em', color: '#fff' }}>EINSTEIN</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: accent }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: accent }} />
            {status === 'thinking' ? 'thinking' : 'ready'}
          </span>
        </div>
        <span style={{ fontSize: 13, lineHeight: 1.5, textAlign: 'center', color: '#9fb8c4' }}>
          Your Heracles AI genius, 24/7 available.
        </span>
        <span style={{ marginTop: 'auto', fontFamily: 'ui-monospace, monospace', fontSize: 10.5, color: '#6d8b99' }}>
          {user?.role ? `role: ${user.role}` : ''}
        </span>
      </div>

      {/* conversation column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, padding: '18px 22px 6px' }}>
          {thread.map(msg => (
            <div key={msg.id} style={{
              flex: 'none', maxWidth: 560, alignSelf: msg.role === 'coach' ? 'flex-end' : 'flex-start',
              background: msg.role === 'coach' ? coachBubbleBg : einsteinBubbleBg,
              color: msg.role === 'coach' ? '#fff' : textColor,
              borderRadius: msg.role === 'coach' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
              padding: '13px 17px', fontSize: 15, lineHeight: 1.5,
            }}>
              {msg.role === 'coach'
                ? msg.text
                : <EinsteinMessage msg={msg} goToRoute={goToRoute} accent={accent} textColor={textColor} mutedColor={mutedColor} onOptionClick={onOptionClick} />}
            </div>
          ))}
          {status === 'thinking' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
              {[0, 0.2, 0.4].map(delay => (
                <span key={delay} style={{ width: 6, height: 6, borderRadius: 999, background: accent, opacity: 0.6 }} />
              ))}
              <span style={{ fontSize: 12.5, color: mutedColor }}>Einstein is checking the registry and squad data…</span>
            </div>
          )}
        </div>

        {!hasChatted && !!suggestions.length && (
          <div style={{ display: 'flex', gap: 10, padding: '0 22px 12px', flexWrap: 'wrap', flex: 'none' }}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => send(s.question)}
                style={{ padding: '8px 15px', borderRadius: 999, background: isDay ? 'rgba(255,255,255,.5)' : 'rgba(255,255,255,.06)', boxShadow: `inset 0 0 0 1px ${isDay ? 'rgba(226,232,237,.9)' : 'rgba(255,255,255,.12)'}`, fontSize: 13.5, color: mutedColor, cursor: 'pointer', border: 'none' }}>
                {s.question}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderTop: `1px solid ${isDay ? 'rgba(237,241,244,.8)' : 'rgba(255,255,255,.08)'}`, flex: 'none' }}>
          <input
            ref={inputRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            disabled={!hasSessionToken}
            placeholder={hasSessionToken ? 'Ask Einstein about Heracles Almelo…' : 'Sign out and back in to enable Einstein chat'}
            style={{
              flex: 1, padding: '13px 18px', borderRadius: 999, border: 'none', outline: 'none',
              background: isDay ? 'rgba(255,255,255,.72)' : 'rgba(255,255,255,.07)', color: textColor, fontSize: 15,
              opacity: hasSessionToken ? 1 : 0.6,
            }}
          />
          <button type="button" onClick={() => send()} disabled={!hasSessionToken || !draft.trim() || status === 'thinking'}
            style={{
              width: 44, height: 44, borderRadius: 999, border: 'none', flex: 'none',
              background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: hasSessionToken && draft.trim() && status !== 'thinking' ? 'pointer' : 'default',
              opacity: hasSessionToken && draft.trim() && status !== 'thinking' ? 1 : 0.5,
            }}>
            <ArrowUp size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
